/**
 * api/client.js — cliente HTTP central da SPA Nava Flow.
 *
 * Responsabilidades:
 *   1. Injetar `Authorization: Bearer <access_token>` em toda requisição.
 *   2. Tratar respostas de erro no formato RFC 7807 (problem+json):
 *      { type, title, status, detail, instance, ... }
 *   3. Em 401 (token expirado mid-request), dispara evento custom
 *      'nava:session-expired' no window → AuthProvider pode interceptar.
 *   4. Em 403 (permissão negada), lança ApiError com status 403.
 *
 * Padrões:
 *   - OWASP: token não exposto em query string ou logs.
 *   - Clean: sem acoplamento ao React — funções puras que recebem `getToken`.
 *   - O cliente é inicializado uma vez via `createApiClient(getToken)` e
 *     reutilizado pelo QueryClient (F0-FE-3).
 *
 * Alinhamento com backend FastAPI (F0-BE-1):
 *   - Resposta de erro: Content-Type: application/problem+json
 *   - Claims JWT esperados: sub, tenant_id, matrix_id, role
 *
 * Uso com @tanstack/react-query:
 *   const { data } = useQuery({ queryKey: ['projects'], queryFn: () => api.get('/projects') });
 */

// ─── Configuração ─────────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

// ─── ApiError — encapsula erros problem+json e erros de rede ─────────────────

export class ApiError extends Error {
  /**
   * @param {number} status        — HTTP status code
   * @param {string} title         — título legível (do problem+json ou fallback)
   * @param {string} [detail]      — detalhe adicional
   * @param {string} [type]        — URI do tipo de problema (RFC 7807)
   * @param {object} [problemBody] — body completo do problem+json
   */
  constructor(status, title, detail, type, problemBody) {
    super(title);
    this.name        = 'ApiError';
    this.status      = status;
    this.title       = title;
    this.detail      = detail   ?? '';
    this.type        = type     ?? 'about:blank';
    this.problemBody = problemBody ?? {};
  }

  /** Verdadeiro se for erro de autenticação. */
  get isUnauthorized() { return this.status === 401; }

  /** Verdadeiro se for erro de autorização. */
  get isForbidden() { return this.status === 403; }

  /** Verdadeiro se for erro de validação. */
  get isValidation() { return this.status === 422; }
}

// ─── Helpers internos ─────────────────────────────────────────────────────────

/**
 * Constrói os headers padrão para toda requisição.
 * @param {string|null} token — access token JWT atual
 * @param {boolean} hasBody   — true se a requisição tem body JSON
 */
function buildHeaders(token, hasBody = false) {
  const headers = new Headers({
    Accept: 'application/json, application/problem+json',
  });

  if (hasBody) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

/**
 * Processa a resposta HTTP e lança ApiError para respostas de erro.
 * Trata problem+json (RFC 7807) automaticamente.
 *
 * @param {Response} response
 * @returns {Promise<unknown>} — body parseado
 * @throws {ApiError}
 */
async function handleResponse(response) {
  // Resposta sem corpo (204 No Content)
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  const isProblem   = contentType.includes('problem+json');
  const isJson      = contentType.includes('application/json') || isProblem;

  let body = null;
  if (isJson) {
    try {
      body = await response.json();
    } catch {
      body = null;
    }
  }

  if (response.ok) {
    return body;
  }

  // ── Tratar 401 — sessão expirada durante requisição ───────────────────────
  if (response.status === 401) {
    // Dispara evento para que o AuthProvider possa reagir (ex.: logout, refresh)
    window.dispatchEvent(new CustomEvent('nava:session-expired', {
      detail: { url: response.url },
    }));
  }

  // ── Extrair detalhes do problem+json ──────────────────────────────────────
  if (isProblem && body) {
    throw new ApiError(
      response.status,
      body.title   ?? statusToTitle(response.status),
      body.detail  ?? '',
      body.type    ?? 'about:blank',
      body,
    );
  }

  // Fallback para erros sem problem+json
  throw new ApiError(
    response.status,
    statusToTitle(response.status),
    typeof body === 'string' ? body : '',
  );
}

/** Título legível para status HTTP (fallback quando não há problem+json). */
function statusToTitle(status) {
  const titles = {
    400: 'Requisição inválida',
    401: 'Não autenticado',
    403: 'Acesso negado',
    404: 'Recurso não encontrado',
    409: 'Conflito',
    422: 'Dados inválidos',
    429: 'Muitas requisições — tente novamente em instantes',
    500: 'Erro interno do servidor',
    502: 'Serviço indisponível',
    503: 'Serviço temporariamente indisponível',
  };
  return titles[status] ?? `Erro HTTP ${status}`;
}

// ─── Factory do cliente ───────────────────────────────────────────────────────

/**
 * createApiClient — inicializa o cliente HTTP com acesso ao token atual.
 *
 * O `getToken` é chamado a cada requisição (não em cache), garantindo que
 * tokens renovados pelo scheduler de refresh sejam usados automaticamente.
 *
 * @param {() => string|null} getToken — função que devolve o access_token atual
 * @returns {ApiClient}
 */
export function createApiClient(getToken) {
  /**
   * Executa uma requisição fetch com autenticação.
   *
   * @param {string} path    — path relativo (ex.: '/projects')
   * @param {RequestInit} [options]
   * @returns {Promise<unknown>}
   * @throws {ApiError}
   */
  async function request(path, options = {}) {
    const token   = getToken();
    const hasBody = Boolean(options.body);
    const headers = buildHeaders(token, hasBody);

    // Mescla headers fornecidos pelo chamador
    if (options.headers) {
      const extra = options.headers instanceof Headers
        ? options.headers
        : new Headers(options.headers);
      extra.forEach((value, key) => headers.set(key, value));
    }

    let response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
      });
    } catch (networkErr) {
      throw new ApiError(0, 'Sem conexão com o servidor', networkErr.message);
    }

    return handleResponse(response);
  }

  return {
    /**
     * POST /path que retorna a Response CRUA (para ler streams SSE via
     * response.body). Injeta o Authorization e Accept: text/event-stream.
     * @param {string} path
     * @param {unknown} body
     * @param {{ signal?: AbortSignal }} [options]
     * @returns {Promise<Response>}
     */
    stream: (path, body, options = {}) => {
      const headers = buildHeaders(getToken(), true);
      headers.set('Accept', 'text/event-stream');
      return fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body ?? {}),
        signal: options.signal,
      });
    },

    /**
     * GET /path
     * @template T
     * @param {string} path
     * @param {RequestInit} [options]
     * @returns {Promise<T>}
     */
    get: (path, options) => request(path, { ...options, method: 'GET' }),

    /**
     * GET /path devolvendo o corpo como TEXTO (arquivos .md, .css…). Erro segue o
     * mesmo tratamento das outras chamadas (ApiError com o detail da API).
     * @param {string} path
     * @returns {Promise<string>}
     */
    getText: async (path) => {
      const headers = buildHeaders(getToken(), false);
      let response;
      try {
        response = await fetch(`${BASE_URL}${path}`, { method: 'GET', headers });
      } catch (networkErr) {
        throw new ApiError(0, 'Sem conexão com o servidor', networkErr.message);
      }
      if (!response.ok) return handleResponse(response);
      return response.text();
    },

    /**
     * GET /path devolvendo o corpo BINÁRIO (.zip…) e o nome que a API sugeriu no
     * `Content-Disposition`. Erro segue o mesmo tratamento das outras chamadas.
     * @param {string} path
     * @returns {Promise<{ blob: Blob, filename: string|null }>}
     */
    getBlob: async (path) => {
      const headers = buildHeaders(getToken(), false);
      let response;
      try {
        response = await fetch(`${BASE_URL}${path}`, { method: 'GET', headers });
      } catch (networkErr) {
        throw new ApiError(0, 'Sem conexão com o servidor', networkErr.message);
      }
      if (!response.ok) return handleResponse(response);
      const disposicao = response.headers.get('content-disposition') || '';
      const achado = /filename="?([^";]+)"?/i.exec(disposicao);
      return { blob: await response.blob(), filename: achado ? achado[1] : null };
    },

    /**
     * POST /path com body JSON
     * @template T
     * @param {string} path
     * @param {unknown} body
     * @param {RequestInit} [options]
     * @returns {Promise<T>}
     */
    post: (path, body, options) =>
      request(path, { ...options, method: 'POST', body: JSON.stringify(body) }),

    /**
     * PUT /path com body JSON
     * @template T
     * @param {string} path
     * @param {unknown} body
     * @param {RequestInit} [options]
     * @returns {Promise<T>}
     */
    put: (path, body, options) =>
      request(path, { ...options, method: 'PUT', body: JSON.stringify(body) }),

    /**
     * PATCH /path com body JSON
     * @template T
     * @param {string} path
     * @param {unknown} body
     * @param {RequestInit} [options]
     * @returns {Promise<T>}
     */
    patch: (path, body, options) =>
      request(path, { ...options, method: 'PATCH', body: JSON.stringify(body) }),

    /**
     * DELETE /path
     * @template T
     * @param {string} path
     * @param {RequestInit} [options]
     * @returns {Promise<T>}
     */
    delete: (path, options) => request(path, { ...options, method: 'DELETE' }),

    /**
     * request — escape hatch para requisições customizadas.
     */
    request,
  };
}

// ─── Hook React para acessar o cliente dentro de componentes ─────────────────
// (injetado pelo ApiProvider — ver api/ApiProvider.jsx)

import { useContext } from 'react';
import { ApiContext } from './ApiContext';

/**
 * useApi — retorna o cliente HTTP autenticado.
 *
 * @returns {ReturnType<typeof createApiClient>}
 */
export function useApi() {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApi deve ser usado dentro de <ApiProvider>');
  }
  return ctx.client;
}
